import { Icon } from "@circle-react-shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon as CompassIcon } from "@circleco/compass/components/Icon";
import { Typography } from "@circleco/compass/components/Typography";
import { Link, useRouterState } from "@tanstack/react-router";

import { isBuildItemChildActive } from "./buildItemActive";
import {
  DOCK_NAV_ACTIVE_CLASSES,
  DOCK_NAV_ROW_CLASSES,
  DOCK_NAV_SUBLINK_CLASSES,
} from "./constants";
import { isPathActive } from "./isPathActive";
import type { BuildNavItem } from "./types";
import { useShouldPreserveLocationState } from "./useShouldPreserveLocationState";

interface CircleAIBuildNavGroupProps {
  isOpen: boolean;
  item: BuildNavItem;
  onToggle: () => void;
}

export const CircleAIBuildNavGroup = ({
  item,
  isOpen,
  onToggle,
}: CircleAIBuildNavGroupProps) => {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const children = item.children ?? [];
  const shouldPreserveLocationState = useShouldPreserveLocationState(item.id);

  const hasActiveChild = isBuildItemChildActive(item, pathname);
  const isActive = hasActiveChild && !isOpen;
  const icon = isActive && item.activeIcon ? item.activeIcon : item.icon;
  const compassIcon =
    isActive && item.compassActiveIcon
      ? item.compassActiveIcon
      : item.compassIcon;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={classNames(
          DOCK_NAV_ROW_CLASSES,
          isActive && DOCK_NAV_ACTIVE_CLASSES,
        )}
      >
        {compassIcon ? (
          <CompassIcon
            name={compassIcon}
            size="md"
            color="primary"
            className="shrink-0"
          />
        ) : (
          <Icon type={icon} size={20} className="shrink-0" />
        )}
        <Typography variant="body-sm" className="flex-1 truncate text-left">
          {item.label}
        </Typography>
        <CompassIcon
          name={isOpen ? "chevron-down" : "chevron-right"}
          size="md"
          color="tertiary"
          className="shrink-0"
        />
      </button>
      {isOpen && (
        <div className="mt-0.5 flex flex-col gap-0.5">
          {children.map(child => (
            <Link
              key={child.id}
              to={child.to}
              {...(shouldPreserveLocationState
                ? { state: true, search: true, hash: true }
                : {})}
              data-dock-active={
                isPathActive({
                  exact: child.exact,
                  pathname,
                  to: child.to,
                }) || undefined
              }
              activeOptions={{
                exact: child.exact ?? false,
                includeSearch: false,
              }}
              className={DOCK_NAV_SUBLINK_CLASSES}
              activeProps={{ className: DOCK_NAV_ACTIVE_CLASSES }}
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden="true"
                    className="text-light flex w-5 shrink-0 items-center justify-center"
                  >
                    <span className="size-0.5 rounded-full bg-current" />
                  </span>
                  <Typography
                    variant="body-sm"
                    color={isActive ? "primary" : "secondary"}
                    truncate
                  >
                    {child.label}
                  </Typography>
                </>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
